import { setCurrentQuestionInfo } from '@actions/quizTaking';
import styled from '@emotion/styled';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import {
  getQuizDetails,
  getCurrentQuestionInfo,
  getAllQuestionsBySection
} from '@selectors/quizTaking';
import { Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Tabs from '@material-ui/core/Tabs/Tabs';
import Tab from '@material-ui/core/Tab/Tab';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Section from './Section';

const SectionsWrapper = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_6B};
  flex-wrap: wrap;
  width: 100%;
  overflow: hidden;
  justify-content: center;

  .MuiTabs-root {
    position: relative;
    min-height: ${Spacings.SPACING_10B};
  }
`;

const StyledTabs = styled(Tabs)`
  overflow-x: hidden;
  align-items: center;
  width: 100%;

  .MuiTab-textColorInherit {
    opacity: 1;
  }

  .MuiTabScrollButton-root {
    opacity: 1;
  }

  .Mui-disabled {
    opacity: 0;
  }

  .MuiTabs-indicator {
    display: none;
  }

  .MuiTouchRipple-root {
    display: none;
  }

  .MuiTab-root {
    max-width: max-content;
    min-width: 10px;
    text-transform: none;
    padding: 0;
    min-height: ${Spacings.SPACING_10B};
  }

  .MuiTabs-scrollButtons {
    border-radius: ${Spacings.SPACING_2B};
    width: ${Spacings.SPACING_10B};
    height: ${Spacings.SPACING_10B};
    background-color: var(--color-base-1);
    box-shadow: ${boxShadowGenerator()};
    position: absolute;
    top: 0;
    bottom: 0;
  }

  .MuiTabs-scrollButtons:nth-child(1) {
    left: 0;
    z-index: 3;
  }

  .MuiTabs-scrollButtons:last-child {
    right: 0;
    z-index: 3;
  }
`;

const SectionList = () => {
  const { query } = useRouter();
  const { quizUID } = query;
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);

  const handleChange = (newValue) => {
    setValue(newValue);
  };

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));

  const { sectionUID } = useSelectorWithShallowEqual(getCurrentQuestionInfo);
  const questionsBySection = useSelectorWithShallowEqual(
    getAllQuestionsBySection
  );

  const [selectedSectionId, setSelectedSectionId] = useState(
    sectionUID ?? quizDetails?.sections[0]?.uid
  );

  useEffect(() => {
    setSelectedSectionId(sectionUID ?? quizDetails?.sections[0]?.uid);

    const indexOfSection =
      quizDetails?.sections?.findIndex(({ uid }) => uid === sectionUID) ?? 0;
    setValue(indexOfSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionUID]);

  const handleSectionClick = (sectionUid) => {
    setSelectedSectionId(sectionUid);
    dispatch(
      setCurrentQuestionInfo({
        sectionUID: sectionUid,
        questionUID: questionsBySection[sectionUid]?.allIds[0]
      })
    );
  };

  return (
    <SectionsWrapper>
      <StyledTabs
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        component="div"
      >
        {quizDetails?.sections?.map((section) => {
          return (
            <Tab
              key={section.uid}
              label={
                <Section
                  key={section.uid}
                  $isSelected={section.uid === selectedSectionId}
                  section={section}
                  onSectionClick={handleSectionClick}
                />
              }
              component="div"
              disableRipple
            />
          );
        })}
      </StyledTabs>
    </SectionsWrapper>
  );
};

export default SectionList;
